import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import CardPaginator from '../../components/ui/CardPaginator'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { useProxyMode } from '../proxy/useProxyMode'
import { managerListColumns } from './components/ManagerListColumns'
import { peopleSummary } from './peopleSummary'
import ManagerListFormDialog from './components/ManagerListFormDialog'
import { useManagerLists } from './hooks/useManagerLists'
import type { ManagerList, ManagerListUpsert } from './types'
import '../admin/admin.css'
import './managerLists.css'

/** Kart listesinin sayfa boyutu (anayasa: tüm listelerde 10 — DataTable de aynı varsayılanı kullanır). */
const CARD_PAGE_SIZE = 10

/** Arama gecikmesi (Page Standards) — her tuşta sunucuya gidilmez. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * Admin → Parametreler → **Liste Yönetimi** ekranı (114; kullanıcı isteği 30.09.2026). Liste
 * (paylaşılan ui/DataTable) + ekle/düzenle pop-up'ı + soft-delete (onaylı). Başlık = menü adı
 * (`nav.admin.managerLists`) — anayasa kuralı.
 *
 * <p><b>Kişiler ayrı bir ekranda değil, aynı pop-up'ta düzenlenir</b> (kullanıcı kararı): "Kaydet" adı
 * ve kişi kümesini sunucuda tek işlemde yazar.
 *
 * <p>Aktif/Pasif <b>yoktur</b> (kullanıcı kararı): yalnız ekle/düzenle/sil — emsal Toplantı Grupları.
 * Mükerrer ad sunucuda engellenir ve hata <b>ad alanının altında</b> gösterilir (toast değil), çünkü
 * kullanıcı hangi alanı düzelteceğini görmeli.
 *
 * <p>SAYFALAMA İSTEMCİ TARAFINDADIR (parametre ölçeği, tek istek): geniş ekranda paylaşılan
 * `ui/DataTable` kendi paginatörünü yönetir, dar ekranda kart listesinin altına `ui/CardPaginator`
 * konur. ARAMA ise SUNUCUDA süzülür (`q`): TR-farkında karşılaştırma (`i` ↔ `İ`) serviste yazılı,
 * istemcide ikinci bir kopyası tutulmaz.
 */
export default function ManagerListsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: Yeni, düzenle ve sil hiç çizilmez.
  const { readOnly } = useProxyMode()

  const [cardFirst, setCardFirst] = useState(0)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [search])

  // Kart sayfası başa YALNIZ arama fiilen değişince döner (emsal HolidayDaysPage: sıfırlamayı gecikme
  // zamanlayıcısının içine koymak, açılıştan 300 ms sonra koşulsuz ateşleyip kullanıcıyı 1. sayfaya
  // geri atan bir yarış üretiyordu).
  useEffect(() => {
    setCardFirst(0)
  }, [debounced])

  const { list, create, update, remove } = useManagerLists(debounced)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ManagerList | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const items = list.data?.content ?? []
  const cardItems = items.slice(cardFirst, cardFirst + CARD_PAGE_SIZE)

  // Liste kısalınca (silme, arama) açık sayfa boşta kalabilir; DURUMUN KENDİSİ başa alınır.
  useEffect(() => {
    setCardFirst((f) => (f >= items.length ? 0 : f))
  }, [items.length])

  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const submitting = create.isPending || update.isPending
  const busyId = remove.isPending ? (remove.variables ?? null) : null

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: ManagerList) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock ve genel hata toast merkezdedir.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('managerLists.errors.duplicateName'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: ManagerListUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('managerLists.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
      return
    }
    create.mutate(payload, {
      onSuccess: () => {
        notify('success', t('managerLists.toast.created'))
        setFormOpen(false)
      },
      onError: handleMutationError,
    })
  }

  const onDelete = (row: ManagerList) => {
    confirmDialog({
      header: t('managerLists.delete.title'),
      message: t('managerLists.delete.confirm', { name: row.name }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('managerLists.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('managerLists.toast.deleted')),
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <ManagerListFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
        readOnly={readOnly}
      />

      <PageHeader
        title={t('nav.admin.managerLists')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            {!readOnly && (
              <Button icon="pi pi-plus" label={t('managerLists.actions.new')} onClick={openCreate} />
            )}
          </>
        }
      />

      <FilterBar
        searchValue={search}
        searchPlaceholder={t('managerLists.search')}
        onSearch={setSearch}
      />

      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState
              title={t('managerLists.empty.title')}
              description={t('managerLists.empty.desc')}
            />
          )}
          {!list.isLoading &&
            cardItems.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <span className="ui-card-sub">
                    {t('managerLists.columns.personCount')}: {row.personCount}
                  </span>
                  {!readOnly && (
                    <div className="ui-card-actions ui-card-actions--compact">
                      <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        aria-label={t('managerLists.actions.editNamed', { name: row.name })}
                        onClick={() => openEdit(row)}
                      />
                      <Button
                        icon="pi pi-trash"
                        rounded
                        text
                        severity="danger"
                        aria-label={t('managerLists.actions.deleteNamed', { name: row.name })}
                        disabled={busyId === row.id}
                        onClick={() => onDelete(row)}
                      />
                    </div>
                  )}
                  {readOnly && (
                    <div className="ui-card-actions ui-card-actions--compact">
                      <Button
                        icon="pi pi-eye"
                        rounded
                        text
                        aria-label={t('managerLists.actions.viewNamed', { name: row.name })}
                        onClick={() => openEdit(row)}
                      />
                    </div>
                  )}
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{row.name}</span>
                </div>
                <span className="ui-card-sub">{peopleSummary(row, t('managerLists.form.noPeople'))}</span>
              </div>
            ))}
          {!list.isLoading && items.length > CARD_PAGE_SIZE && (
            <CardPaginator
              first={cardFirst}
              rows={CARD_PAGE_SIZE}
              totalRecords={items.length}
              onPage={setCardFirst}
            />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          emptyMessage={
            <EmptyState
              title={t('managerLists.empty.title')}
              description={t('managerLists.empty.desc')}
            />
          }
        >
          {managerListColumns({ t, busyId, onEdit: openEdit, onDelete, readOnly })}
        </DataTable>
      )}
    </div>
  )
}
